// @flow
import { useEffect, useRef } from 'react';

type ButtonConfig = {|
	disabled?: boolean,
	styles?: Object,
	preventNavigation?: boolean,
	customHandler?: (event: Event, button: HTMLElement) => void
|};

const useDisableHtmlButtons = (
	htmlContent: string,
	config: ButtonConfig = {}
) => {
	const contentRef = useRef(null);
	
	useEffect(() => {
		if (!contentRef.current) return;
		
		const container = contentRef.current;
		const buttons = container.querySelectorAll('a, button');
		
		const handlers = [];
		
		buttons.forEach((button) => {
			if (config.disabled) {
				// Create click handler
				const clickHandler = (e) => {
					e.preventDefault();
					e.stopPropagation();
					
					if (config.customHandler) {
						config.customHandler(e, button);
					}
					
					return false;
				};
				
				// Add event listener
				button.addEventListener('click', clickHandler, true);
				handlers.push({ element: button, handler: clickHandler });
				
				// Apply styles
				if (config.preventNavigation && button.href) {
					button.removeAttribute('href');
				}
				
				// Default disabled styles
				const defaultStyles = {
					cursor: 'not-allowed',
					pointerEvents: 'none',
					opacity: '0.5',
					backgroundColor: '#f5f5f5',
					color: '#999999'
				};
				
				const finalStyles = { ...defaultStyles, ...config.styles };
				
				Object.assign(button.style, finalStyles);
				
				// Accessibility
				button.setAttribute('aria-disabled', 'true');
				button.setAttribute('tabindex', '-1');
			}
		});
		
		// Cleanup
		return () => {
			handlers.forEach(({ element, handler }) => {
				element.removeEventListener('click', handler, true);
				element.style.cursor = '';
				element.style.pointerEvents = '';
				element.removeAttribute('aria-disabled');
				element.removeAttribute('tabindex');
			});
		};
	}, [htmlContent, config]);
	
	return contentRef;
};

export default useDisableHtmlButtons;